import styles from './AdminUserManagement.module.css';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
} from 'firebase/auth';
import {
  collection,
  getDocs,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { CircularProgress } from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  VpnKey as VpnKeyIcon,
  PersonOff as PersonOffIcon,
  HowToReg as HowToRegIcon,
  People as PeopleIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  WorkspacePremium as WorkspacePremiumIcon,
  LocalShipping as LocalShippingIcon,
  Handshake as HandshakeIcon,
  FactCheck as FactCheckIcon,
} from '@mui/icons-material';
import { db, auth, secondaryAuth } from '../../firebase';

// These strings are what land in the user doc's `roles` array, and both
// firestore.rules and src/roles.js match on them verbatim — add here and there
// together or the role grants nothing.
const AVAILABLE_ROLES = ['admin', 'sales', 'driver', 'reconciliation', 'owner'];

const ROLE_CLASS = {
  admin: 'roleAdmin',
  sales: 'roleSales',
  owner: 'roleOwner',
  driver: 'roleDriver',
  reconciliation: 'roleReconciliation',
};

const ROLE_ICON = {
  admin: AdminPanelSettingsIcon,
  sales: HandshakeIcon,
  driver: LocalShippingIcon,
  owner: WorkspacePremiumIcon,
  reconciliation: FactCheckIcon,
};

// Random one-time password. The new user never sees this - they set their
// own via the password reset email sent after account creation.
const generateTempPassword = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%';
  let out = '';
  for (let i = 0; i < 20; i++) {
    out += chars[Math.floor(Math.random() * chars.length)];
  }
  return out;
};

const RoleBadge = ({ role }) => (
  <span className={`${styles.roleBadge} ${styles[ROLE_CLASS[role]] || styles.roleUnknown}`}>
    {role.charAt(0).toUpperCase() + role.slice(1)}
  </span>
);

const StatusBadge = ({ disabled }) => (
  <span className={`${styles.statusBadge} ${disabled ? styles.statusDisabled : styles.statusActive}`}>
    <span className={styles.statusDot} />
    {disabled ? 'Disabled' : 'Active'}
  </span>
);

const RoleCheckboxGroup = ({ selectedRoles, onToggle }) => (
  <div className="flex flex-wrap gap-2">
    {AVAILABLE_ROLES.map(role => {
      const checked = selectedRoles.includes(role);
      return (
        <label
          key={role}
          className={`${styles.checkboxRow} ${checked ? styles.checkboxRowChecked : ''}`}
        >
          <input type="checkbox" checked={checked} onChange={() => onToggle(role)} />
          {role.charAt(0).toUpperCase() + role.slice(1)}
        </label>
      );
    })}
  </div>
);

const StatTile = ({ icon: Icon, label, value, tone }) => (
  <div className={styles.statTile}>
    <div className={`${styles.statIcon} ${styles[tone]}`}>
      <Icon style={{ fontSize: 18 }} />
    </div>
    <div>
      <div className={styles.statValue}>{value}</div>
      <div className={styles.statLabel}>{label}</div>
    </div>
  </div>
);

const AdminUserManagement = () => {
  const [authUser] = useAuthState(auth);

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [listError, setListError] = useState('');

  // Toolbar state
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilters, setRoleFilters] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all'); // all | active | disabled
  const [sortBy, setSortBy] = useState('newest'); // newest | oldest | name-asc | name-desc

  // Create modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [createError, setCreateError] = useState('');

  // Edit modal state
  const [editingUser, setEditingUser] = useState(null);
  const [editName, setEditName] = useState('');
  const [editRoles, setEditRoles] = useState([]);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState('');

  // Delete modal state
  const [deletingUser, setDeletingUser] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  // Row-level transient state
  const [togglingId, setTogglingId] = useState(null);
  const [resetSentId, setResetSentId] = useState(null);
  const [rowError, setRowError] = useState('');

  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    setListError('');
    try {
      const snap = await getDocs(collection(db, 'users'));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setUsers(list);
    } catch (err) {
      console.error('Error fetching users:', err);
      setListError('Failed to load users.');
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // ---- Stats ----
  const stats = useMemo(() => {
    const total = users.length;
    const disabledCount = users.filter(u => u.disabled).length;
    return {
      total,
      active: total - disabledCount,
      disabled: disabledCount,
    };
  }, [users]);

  // ---- Filtering + sorting ----
  const visibleUsers = useMemo(() => {
    let list = [...users];

    if (searchTerm.trim()) {
      const q = searchTerm.trim().toLowerCase();
      list = list.filter(u =>
        (u.name || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q)
      );
    }

    if (roleFilters.length > 0) {
      list = list.filter(u => (u.roles || []).some(r => roleFilters.includes(r)));
    }

    if (statusFilter === 'active') list = list.filter(u => !u.disabled);
    if (statusFilter === 'disabled') list = list.filter(u => u.disabled);

    switch (sortBy) {
      case 'oldest':
        list.sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
        break;
      case 'name-asc':
        list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        break;
      case 'name-desc':
        list.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
        break;
      case 'newest':
      default:
        list.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
    }

    return list;
  }, [users, searchTerm, roleFilters, statusFilter, sortBy]);

  const toggleRoleFilter = (role) => {
    setRoleFilters(prev => prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]);
  };

  // ---- Create user ----
  const toggleSelectedRole = (role) => {
    setSelectedRoles(prev => prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]);
  };

  const openCreateModal = () => {
    setEmail('');
    setName('');
    setSelectedRoles([]);
    setCreateError('');
    setShowCreateModal(true);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreateError('');

    if (!email.trim() || !name.trim() || selectedRoles.length === 0) {
      setCreateError('Please provide an email, name, and at least one role.');
      return;
    }

    setSubmitting(true);
    try {
      const tempPassword = generateTempPassword();

      // Created on the SECONDARY auth instance so the admin's own session
      // (on the primary `auth` instance) is never signed out of.
      const cred = await createUserWithEmailAndPassword(secondaryAuth, email.trim(), tempPassword);
      const newUid = cred.user.uid;

      // Immediately clear the secondary session - we only needed it to mint the uid.
      await signOut(secondaryAuth);

      await setDoc(doc(db, 'users', newUid), {
        userId: newUid,
        email: email.trim(),
        name: name.trim(),
        roles: selectedRoles,
        disabled: false,
        createdAt: serverTimestamp(),
        lastModified: serverTimestamp(),
      });

      // New user sets their own password rather than the admin handling raw credentials.
      await sendPasswordResetEmail(auth, email.trim());

      setShowCreateModal(false);
      fetchUsers();
    } catch (err) {
      console.error('Error creating user:', err);
      const message =
        err.code === 'auth/email-already-in-use'
          ? 'That email is already registered.'
          : err.code === 'auth/invalid-email'
          ? 'That email address looks invalid.'
          : err.message || 'Failed to create user.';
      setCreateError(message);
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Edit user ----
  const openEditModal = (u) => {
    setEditingUser(u);
    setEditName(u.name || '');
    setEditRoles(u.roles || []);
    setEditError('');
  };

  const toggleEditRole = (role) => {
    setEditRoles(prev => prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]);
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    setEditError('');

    if (!editName.trim() || editRoles.length === 0) {
      setEditError('Name and at least one role are required.');
      return;
    }

    setEditSubmitting(true);
    try {
      await updateDoc(doc(db, 'users', editingUser.id), {
        name: editName.trim(),
        roles: editRoles,
        lastModified: serverTimestamp(),
      });
      setUsers(prev => prev.map(u =>
        u.id === editingUser.id ? { ...u, name: editName.trim(), roles: editRoles } : u
      ));
      setEditingUser(null);
    } catch (err) {
      console.error('Error updating user:', err);
      setEditError(err.message || 'Failed to update user.');
    } finally {
      setEditSubmitting(false);
    }
  };

  // ---- Enable / disable ----
  const handleToggleDisabled = async (u) => {
    setRowError('');
    setTogglingId(u.id);
    try {
      const nextDisabled = !u.disabled;
      await updateDoc(doc(db, 'users', u.id), {
        disabled: nextDisabled,
        lastModified: serverTimestamp(),
      });
      setUsers(prev => prev.map(row => row.id === u.id ? { ...row, disabled: nextDisabled } : row));
    } catch (err) {
      console.error('Error toggling user status:', err);
      setRowError(`Failed to update status for ${u.name || u.email}.`);
    } finally {
      setTogglingId(null);
    }
  };

  // ---- Reset password ----
  const handleResetPassword = async (u) => {
    setRowError('');
    if (!u.email) return;
    try {
      await sendPasswordResetEmail(auth, u.email);
      setResetSentId(u.id);
      setTimeout(() => setResetSentId(null), 2500);
    } catch (err) {
      console.error('Error sending reset email:', err);
      setRowError(`Failed to send reset email to ${u.email}.`);
    }
  };

  // ---- Delete ----
  const handleDeleteUser = async () => {
    if (!deletingUser) return;
    setDeleteSubmitting(true);
    try {
      await deleteDoc(doc(db, 'users', deletingUser.id));
      setUsers(prev => prev.filter(u => u.id !== deletingUser.id));
      setDeletingUser(null);
    } catch (err) {
      console.error('Error deleting user:', err);
      setRowError(`Failed to delete ${deletingUser.name || deletingUser.email}.`);
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const formatDate = (u) => u.createdAt?.seconds
    ? new Date(u.createdAt.seconds * 1000).toLocaleDateString()
    : 'N/A';

  const hasActiveFilters = searchTerm.trim() || roleFilters.length > 0 || statusFilter !== 'all';

  return (
    <div className="w-full flex justify-center">
      <div className="w-full max-w-full lg:max-w-6xl p-4 md:p-6 lg:px-8">
        <header className="mb-4 md:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-lg md:text-xl font-semibold text-gray-800">User Administration</h1>
            <p className="text-sm text-gray-500 mt-1">Manage accounts, roles, and access.</p>
          </div>
          <button onClick={openCreateModal} className={styles.primaryButton}>
            <AddIcon style={{ fontSize: 16 }} className="mr-1" />
            Add User
          </button>
        </header>

        {/* Stats */}
        <div className={styles.statsRow}>
          <StatTile icon={PeopleIcon} label="Total Users" value={stats.total} tone="statTotal" />
          <StatTile icon={HowToRegIcon} label="Active" value={stats.active} tone="statActive" />
          <StatTile icon={PersonOffIcon} label="Disabled" value={stats.disabled} tone="statDisabled" />
        </div>

        {rowError && <div className={`${styles.alertError} mb-4`}>{rowError}</div>}

        {/* Toolbar */}
        <div className={`${styles.card} mb-4`}>
          <div className="flex flex-col lg:flex-row lg:items-center gap-3">
            <div className={styles.searchWrap}>
              <SearchIcon style={{ fontSize: 16 }} className={styles.searchIcon} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or email..."
                className={styles.searchInputWithIcon}
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {AVAILABLE_ROLES.map(role => {
                const active = roleFilters.includes(role);
                const Icon = ROLE_ICON[role];
                return (
                  <button
                    key={role}
                    type="button"
                    onClick={() => toggleRoleFilter(role)}
                    className={`${styles.filterChip} ${active ? styles.filterChipActive : ''}`}
                  >
                    <Icon style={{ fontSize: 13 }} className="mr-1" />
                    {role.charAt(0).toUpperCase() + role.slice(1)}
                  </button>
                );
              })}
            </div>

            <div className={styles.segmented}>
              {['all', 'active', 'disabled'].map(opt => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setStatusFilter(opt)}
                  className={`${styles.segmentedOption} ${statusFilter === opt ? styles.segmentedOptionActive : ''}`}
                >
                  {opt.charAt(0).toUpperCase() + opt.slice(1)}
                </button>
              ))}
            </div>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className={styles.sortSelect}
            >
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="name-asc">Name A–Z</option>
              <option value="name-desc">Name Z–A</option>
            </select>
          </div>
        </div>

        {/* Desktop table */}
        <div className={`${styles.tableContainer} hidden md:block`}>
          <div className="overflow-x-auto">
            <table className="w-full table-auto min-w-max">
              <thead>
                <tr className="bg-gray-100 text-left">
                  <th className="p-3 whitespace-nowrap">Name</th>
                  <th className="p-3 whitespace-nowrap">Email</th>
                  <th className="p-3 whitespace-nowrap">Roles</th>
                  <th className="p-3 whitespace-nowrap">Status</th>
                  <th className="p-3 whitespace-nowrap">Created</th>
                  <th className="p-3 whitespace-nowrap text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loadingUsers ? (
                  <tr><td colSpan={6} className="p-6 text-center text-gray-500">
                    <CircularProgress size={16} className="mr-2" style={{ verticalAlign: 'middle' }} />
                    Loading users...
                  </td></tr>
                ) : listError ? (
                  <tr><td colSpan={6} className="p-6 text-center text-red-600">{listError}</td></tr>
                ) : visibleUsers.length === 0 ? (
                  <tr><td colSpan={6} className="p-6 text-center text-gray-500">
                    {hasActiveFilters ? 'No users match your filters.' : 'No users found.'}
                  </td></tr>
                ) : (
                  visibleUsers.map(u => {
                    const isSelf = authUser && u.id === authUser.uid;
                    return (
                      <tr key={u.id} className={styles.tableRow}>
                        <td className="p-3 whitespace-nowrap font-medium text-gray-800">
                          {u.name || 'N/A'}{isSelf && <span className={styles.youTag}>You</span>}
                        </td>
                        <td className="p-3 whitespace-nowrap text-gray-600">{u.email || 'N/A'}</td>
                        <td className="p-3 whitespace-nowrap">
                          {(u.roles || []).map(r => <RoleBadge key={r} role={r} />)}
                        </td>
                        <td className="p-3 whitespace-nowrap"><StatusBadge disabled={u.disabled} /></td>
                        <td className="p-3 whitespace-nowrap text-gray-500">{formatDate(u)}</td>
                        <td className="p-3 whitespace-nowrap">
                          {isSelf ? (
                            <div className="text-right text-xs text-gray-400 italic">Manage elsewhere</div>
                          ) : (
                            <div className="flex items-center justify-end gap-1">
                              <button
                                title="Edit user"
                                onClick={() => openEditModal(u)}
                                className={styles.iconButton}
                              >
                                <EditIcon style={{ fontSize: 15 }} />
                              </button>
                              <button
                                title={resetSentId === u.id ? 'Email sent!' : 'Send password reset'}
                                onClick={() => handleResetPassword(u)}
                                className={styles.iconButton}
                              >
                                {resetSentId === u.id ? <span className={styles.sentTick}>✓</span> : <VpnKeyIcon style={{ fontSize: 15 }} />}
                              </button>
                              <button
                                title={u.disabled ? 'Enable user' : 'Disable user'}
                                onClick={() => handleToggleDisabled(u)}
                                disabled={togglingId === u.id}
                                className={styles.iconButton}
                              >
                                {togglingId === u.id ? (
                                  <CircularProgress size={15} />
                                ) : u.disabled ? (
                                  <HowToRegIcon style={{ fontSize: 15 }} />
                                ) : (
                                  <PersonOffIcon style={{ fontSize: 15 }} />
                                )}
                              </button>
                              <button
                                title="Delete user"
                                onClick={() => setDeletingUser(u)}
                                className={`${styles.iconButton} ${styles.iconButtonDanger}`}
                              >
                                <DeleteIcon style={{ fontSize: 15 }} />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden flex flex-col gap-3">
          {loadingUsers ? (
            <div className={`${styles.card} text-center text-gray-500`}>
              <CircularProgress size={16} className="mr-2" style={{ verticalAlign: 'middle' }} />
              Loading users...
            </div>
          ) : listError ? (
            <div className={`${styles.card} text-center text-red-600`}>{listError}</div>
          ) : visibleUsers.length === 0 ? (
            <div className={`${styles.card} text-center text-gray-500`}>
              {hasActiveFilters ? 'No users match your filters.' : 'No users found.'}
            </div>
          ) : (
            visibleUsers.map(u => {
              const isSelf = authUser && u.id === authUser.uid;
              return (
                <div key={u.id} className={styles.userCard}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-medium text-gray-800">
                        {u.name || 'N/A'}{isSelf && <span className={styles.youTag}>You</span>}
                      </div>
                      <div className="text-sm text-gray-500">{u.email || 'N/A'}</div>
                    </div>
                    <StatusBadge disabled={u.disabled} />
                  </div>
                  <div className="mt-2">{(u.roles || []).map(r => <RoleBadge key={r} role={r} />)}</div>
                  <div className="mt-2 text-xs text-gray-400">Created {formatDate(u)}</div>
                  {!isSelf && (
                    <div className="mt-3 flex items-center gap-2">
                      <button onClick={() => openEditModal(u)} className={styles.cardActionButton}>
                        <EditIcon style={{ fontSize: 14 }} className="mr-1" /> Edit
                      </button>
                      <button onClick={() => handleResetPassword(u)} className={styles.cardActionButton}>
                        {resetSentId === u.id ? '✓ Sent' : <><VpnKeyIcon style={{ fontSize: 14 }} className="mr-1" /> Reset</>}
                      </button>
                      <button onClick={() => handleToggleDisabled(u)} className={styles.cardActionButton}>
                        {togglingId === u.id ? (
                          <CircularProgress size={14} />
                        ) : u.disabled ? (
                          <><HowToRegIcon style={{ fontSize: 14 }} className="mr-1" /> Enable</>
                        ) : (
                          <><PersonOffIcon style={{ fontSize: 14 }} className="mr-1" /> Disable</>
                        )}
                      </button>
                      <button
                        onClick={() => setDeletingUser(u)}
                        className={`${styles.cardActionButton} ${styles.cardActionButtonDanger}`}
                      >
                        <DeleteIcon style={{ fontSize: 14 }} />
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="mt-3 text-xs text-gray-400 text-center">
          Showing {visibleUsers.length} of {users.length} users
        </div>

        {/* Create user modal */}
        {showCreateModal && (
          <div className={styles.modalOverlay}>
            <div className={styles.modal}>
              <div className={styles.modalHeader}>
                <h3 className={styles.modalTitle}>Add User</h3>
                <button onClick={() => setShowCreateModal(false)} className={styles.iconButton}>
                  <CloseIcon style={{ fontSize: 16 }} />
                </button>
              </div>
              <form onSubmit={handleCreateUser} className="flex flex-col gap-4">
                {createError && <div className={styles.alertError}>{createError}</div>}

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Full name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Jane Doe"
                    className={styles.searchInput}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="jane@company.com"
                    className={styles.searchInput}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Roles</label>
                  <RoleCheckboxGroup selectedRoles={selectedRoles} onToggle={toggleSelectedRole} />
                </div>

                <div className={styles.modalActions}>
                  <button type="button" onClick={() => setShowCreateModal(false)} className={styles.cancelButton}>
                    Cancel
                  </button>
                  <button type="submit" disabled={submitting} className={styles.primaryButton}>
                    {submitting ? 'Creating...' : 'Create User'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit user modal */}
        {editingUser && (
          <div className={styles.modalOverlay}>
            <div className={styles.modal}>
              <div className={styles.modalHeader}>
                <h3 className={styles.modalTitle}>Edit User</h3>
                <button onClick={() => setEditingUser(null)} className={styles.iconButton}>
                  <CloseIcon style={{ fontSize: 16 }} />
                </button>
              </div>
              <form onSubmit={handleEditUser} className="flex flex-col gap-4">
                {editError && <div className={styles.alertError}>{editError}</div>}

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Full name</label>
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className={styles.searchInput}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                  <input type="email" value={editingUser.email || ''} disabled className={styles.searchInputDisabled} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Roles</label>
                  <RoleCheckboxGroup selectedRoles={editRoles} onToggle={toggleEditRole} />
                </div>

                <div className={styles.modalActions}>
                  <button type="button" onClick={() => setEditingUser(null)} className={styles.cancelButton}>
                    Cancel
                  </button>
                  <button type="submit" disabled={editSubmitting} className={styles.primaryButton}>
                    {editSubmitting ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete confirmation modal */}
        {deletingUser && (
          <div className={styles.modalOverlay}>
            <div className={styles.deleteModal}>
              <h3 className={styles.deleteModalTitle}>Delete User</h3>
              <p className={styles.deleteModalText}>
                Remove <span className="font-semibold">{deletingUser.name || deletingUser.email}</span> and revoke
                all app access? This cannot be undone.
              </p>
              <div className={styles.deleteModalActions}>
                <button onClick={() => setDeletingUser(null)} className={styles.cancelButton}>
                  Cancel
                </button>
                <button onClick={handleDeleteUser} disabled={deleteSubmitting} className={styles.confirmDeleteButton}>
                  {deleteSubmitting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUserManagement;
